/**
 * 後台 — 販售品項編輯 / 新增
 *
 * Route:
 *   /admin/products/new — 新增
 *   /admin/products/:id — 編輯
 *
 * 必填:product_id(僅新增時可改)、category、name、price
 * 選填:spec / bottle_count / shipping_fee / tagline / image_filename / sort_order
 * 上下架:active toggle
 * 前台隱藏:storefront_hidden(LINE 卡+選購頁不顯示,人工建單仍可選 — 加贈 SKU 用)
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  adminGetProduct,
  adminListProductCategories,
  adminUpsertProduct,
  adminUploadProductImage,
  adminDeleteProduct,
  type ProductCategoryMeta,
} from "../../api/admin";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


const PRODUCT_IMAGE_BASE = "https://storage.googleapis.com/yannwo-product-images";


export default function ProductEdit() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [cats, setCats] = useState<ProductCategoryMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [productId, setProductId] = useState("");
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [spec, setSpec] = useState("");
  const [price, setPrice] = useState("");
  const [bottleCount, setBottleCount] = useState("");
  const [shippingFee, setShippingFee] = useState("");
  const [tagline, setTagline] = useState("");
  const [imageFilename, setImageFilename] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [active, setActive] = useState(true);
  const [storefrontHidden, setStorefrontHidden] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const categories = await adminListProductCategories();
        setCats(categories);
        if (!isNew && id) {
          const p = await adminGetProduct(id);
          setProductId(p.id);
          setCategory(p.category);
          setName(p.name);
          setSpec(p.spec);
          setPrice(String(p.price || ""));
          setBottleCount(String(p.bottle_count || ""));
          setShippingFee(String(p.shipping_fee || ""));
          setTagline(p.tagline);
          setImageFilename(p.image_filename);
          setSortOrder(String(p.sort_order || ""));
          setActive(p.active);
          setStorefrontHidden(!!p.storefront_hidden);
        }
      } catch {
        showToast("載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, role]);

  async function save() {
    if (!productId.trim()) {
      showToast("請填寫商品代號(product_id)", "error");
      return;
    }
    if (!category) {
      showToast("請選擇分類", "error");
      return;
    }
    if (!name.trim()) {
      showToast("請填寫商品名", "error");
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminUpsertProduct({
        product_id: productId.trim(),
        category,
        name: name.trim(),
        spec: spec.trim(),
        price: parseInt(price, 10) || 0,
        bottle_count: parseInt(bottleCount, 10) || 0,
        shipping_fee: parseInt(shippingFee, 10) || 0,
        tagline: tagline.trim(),
        image_filename: imageFilename.trim(),
        sort_order: parseInt(sortOrder, 10) || 999,
        active,
        storefront_hidden: storefrontHidden,
      });
      if (r.status === "ok") {
        showToast(r.created ? "已新增" : "已更新", "success");
        navigate("/admin/products");
      } else if (r.status === "invalid_product_id_chars") {
        showToast("商品代號僅限英數字 + 底線", "error");
      } else {
        showToast(`儲存未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  // 選檔 → 自動上傳 GCS → 自動填檔名(仍需按「儲存變更」才寫進商品)
  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";   // 清掉 input,重選同一檔也會再觸發
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast("圖片超過 10MB,請壓縮後再上傳", "error");
      return;
    }
    setUploading(true);
    try {
      const r = await adminUploadProductImage(file);
      if (r.status === "ok" && r.filename) {
        setImageFilename(r.filename);
        showToast("圖片已上傳並填入檔名,記得按「儲存變更」", "success");
      } else if (r.status === "invalid_file_type") {
        showToast("僅支援 jpg / png / webp", "error");
      } else if (r.status === "file_too_large") {
        showToast("圖片超過 10MB,請壓縮後再上傳", "error");
      } else {
        showToast(`上傳未成功:${r.status}`, "error");
      }
    } catch {
      showToast("上傳連線異常,請再試一次", "error");
    } finally {
      setUploading(false);
    }
  }

  async function remove() {
    if (!confirm("確定要刪除這筆商品?(建議改用「下架」軟下架)")) return;
    if (!id || isNew) return;
    setSubmitting(true);
    try {
      const r = await adminDeleteProduct(id);
      if (r.status === "ok") {
        showToast("已刪除", "success");
        navigate("/admin/products");
      } else {
        showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/admin/products")} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">{isNew ? "新增商品" : "編輯商品"}</h1>
      </header>

      <div className="px-6 mt-6 max-w-2xl space-y-4">
        <div className="card space-y-4">
          <div>
            <label className="label">商品代號 *</label>
            <input
              className="input"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={!isNew}
              placeholder="如 fang_cun、liu_bai(限英數 + 底線)"
            />
            {!isNew && <div className="text-xs text-muted mt-1">商品代號建立後不可變更</div>}
          </div>

          <div>
            <label className="label">分類 *</label>
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">請選擇</option>
              {cats.map((c) => (
                <option key={c.key} value={c.key}>{c.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">商品名 *</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="霽雲・◯◯"
            />
          </div>

          <div>
            <label className="label">規格</label>
            <input
              className="input"
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
              placeholder="60ml × 6 入 / 108g(3 個月份)…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">售價(NT$)*</label>
              <input
                className="input"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="3600"
              />
            </div>
            <div>
              {/* 單位隨商品線走 — 燕盞(dry)是「盒」不是「瓶」,2026-09-20 加第四品類後
                  這裡若還寫死「瓶數」,設定燕盞份數的人會被誤導 */}
              <label className="label">
                {category === "dry" ? "盒數(存摺換算用)" : "瓶數(存摺換算用)"}
              </label>
              <input
                className="input"
                type="number"
                value={bottleCount}
                onChange={(e) => setBottleCount(e.target.value)}
                placeholder={category === "dry" ? "一盒 108 克,填這個方案含幾盒" : "6(自燉線可空)"}
              />
              {category === "dry" && (
                <div className="text-xxs text-muted mt-1 leading-relaxed">
                  燕盞以「盒」計(一盒 108 克)。沒填或填 0 的話,這項商品無法用於送禮。
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">運費</label>
              <input
                className="input"
                type="number"
                value={shippingFee}
                onChange={(e) => setShippingFee(e.target.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="label">排序(數字小在前)</label>
              <input
                className="input"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                placeholder="10、20、30…"
              />
            </div>
          </div>

          <div>
            <label className="label">標語(tagline,Bot 卡片顯示)</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="美學的縮影 — 不妥協的滋養"
            />
          </div>

          <div>
            <label className="label">主圖</label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={imageFilename}
                onChange={(e) => setImageFilename(e.target.value)}
                placeholder="product-fang-cun.jpg"
              />
              <label
                className={`btn-secondary shrink-0 cursor-pointer flex items-center ${
                  uploading ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                {uploading ? "上傳中…" : "上傳圖片"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={onPickImage}
                />
              </label>
            </div>
            {imageFilename && (
              <div className="mt-2">
                <img
                  src={`${PRODUCT_IMAGE_BASE}/${imageFilename}`}
                  alt="預覽"
                  className="w-40 h-30 object-cover rounded border border-line"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <div className="text-xs text-muted mt-1">
                  預覽:{PRODUCT_IMAGE_BASE}/{imageFilename}
                </div>
              </div>
            )}
            <div className="text-xs text-muted mt-1">
              按「上傳圖片」選檔即自動上傳並填入檔名(jpg / png / webp,10MB 內),完成後記得按「儲存變更」
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="active"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="active" className="text-sm cursor-pointer">
              上架中(取消勾選即下架,不會刪除歷史資料)
            </label>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="storefront_hidden"
              checked={storefrontHidden}
              onChange={(e) => setStorefrontHidden(e.target.checked)}
              className="w-4 h-4 mt-0.5"
            />
            <label htmlFor="storefront_hidden" className="text-sm cursor-pointer">
              不在前台顯示
              <div className="text-xs text-muted mt-0.5">
                勾選後 LINE 商品卡與選購頁看不到,但人工建單仍可選(加贈、特殊價品項用)
              </div>
            </label>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate("/admin/products")}
            className="btn-secondary flex-1"
            disabled={submitting}
          >
            取消
          </button>
          <button
            onClick={save}
            className="btn-primary flex-1"
            disabled={submitting}
          >
            {submitting ? "儲存中…" : isNew ? "新增" : "儲存變更"}
          </button>
        </div>

        {!isNew && (
          <div className="card border-coral/30 bg-coral/5">
            <div className="text-sm font-bold text-coral mb-2">硬刪除(慎用)</div>
            <div className="text-xs text-muted mb-3">
              建議用上方「上架中」取消勾選軟下架。若 SKU 從未售出且確定不要,可硬刪除。
            </div>
            <button
              onClick={remove}
              className="text-sm text-coral underline disabled:opacity-50"
              disabled={submitting}
            >
              刪除此商品
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
